import {
  MIRA_PERIOD_ROLLING_12,
  MIRA_PERIOD_ROLLING_12_PREVIOUS,
  MIRA_PERIOD_YEAR_PREFIX,
  MIRA_PERIOD_YEAR_TO_DATE,
  MiraPublishedPeriod,
  MiraSectionDocument,
} from "@/data/models/Mira";

/**
 * What a period's changes compare with (Hans, #2610137; Taimoor, 2026-11-02): "Vorherige Periode", the window
 * of the same length directly before (the rolling year one month back, the year to date one year back), or
 * "Letzte 12 Monate", the twelve months directly before. IRES Cloud publishes both as keys of other periods
 * (`baselines` on an offered period).
 */
export type MiraBaseline = "previous" | "previous12";

export const MIRA_BASELINE_PREVIOUS: MiraBaseline = "previous";
export const MIRA_BASELINE_PREVIOUS_12: MiraBaseline = "previous12";

/**
 * The totals (`count` metrics in IRES Cloud's registry) for a document written before it carried
 * `countMetrics` (2026-11-02). Pinned by a test against the list IRES Cloud publishes.
 */
export const MIRA_COUNT_METRICS: readonly string[] = [
  "MIRA-PRJ-001",
  "MIRA-PRJ-003",
  "MIRA-PRJ-006",
  "MIRA-PRJ-007",
  "MIRA-PRJ-013",
  "MIRA-PRJ-017",
  "MIRA-DEV-001",
  "MIRA-DEV-002",
  "MIRA-DEV-004",
  "MIRA-DEV-008",
  "MIRA-INS-001",
];

/** The document's totals, as IRES Cloud lists them, or the fallback for an older document. */
export function countMetricIds(document: Pick<MiraSectionDocument, "countMetrics"> | null | undefined): readonly string[] {
  return document?.countMetrics ?? MIRA_COUNT_METRICS;
}

/**
 * The baseline a document written before the baselines implies: `comparesTo` as "Vorherige Periode", and as
 * "Letzte 12 Monate" the window IRES Cloud already published for the rolling year, the year to date and a year.
 */
function legacyBaseline(periodKey: string, period: MiraPublishedPeriod, choice: MiraBaseline): string | undefined {
  if (choice === MIRA_BASELINE_PREVIOUS) {
    return period.comparesTo;
  }
  if (periodKey === MIRA_PERIOD_ROLLING_12) {
    return MIRA_PERIOD_ROLLING_12_PREVIOUS;
  }
  if (periodKey === MIRA_PERIOD_YEAR_TO_DATE || periodKey.startsWith(MIRA_PERIOD_YEAR_PREFIX)) {
    return `${MIRA_PERIOD_YEAR_PREFIX}${Number(period.from.slice(0, 4)) - 1}`;
  }
  return undefined;
}

/** The key of a period's baseline, where the document publishes it, else `null` (no change is shown). */
export function baselineKey(
  document: Pick<MiraSectionDocument, "periods"> | null | undefined,
  periodKey: string,
  choice: MiraBaseline
): string | null {
  const period = document?.periods[periodKey];
  if (!document || !period) {
    return null;
  }
  const target = period.baselines ? period.baselines[choice] : legacyBaseline(periodKey, period, choice);
  return target && document.periods[target] ? target : null;
}

/**
 * The one comparison a period has (Hans, #2611021, 2026-11-04: "no need to select the comparison"): Letzte 12
 * Monate against the 12 months before, Dieses Jahr against the same months last year, a year against the year
 * before. The choice in the calendar is gone, and with it the rolling year one month back (2026-10-30), which
 * IRES Cloud still publishes as the rolling year's "Vorherige Periode" (dropped from IRES Office for now,
 * Taimoor 2026-11-04).
 */
export function impliedBaseline(periodKey: string): MiraBaseline {
  return periodKey === MIRA_PERIOD_ROLLING_12 ? MIRA_BASELINE_PREVIOUS_12 : MIRA_BASELINE_PREVIOUS;
}

/** The i18n key naming a period's comparison in the header: "Die 12 Monate davor", "Dieselben Monate im Vorjahr", "Vorjahr". */
export function baselineNameKey(periodKey: string): string {
  if (periodKey === MIRA_PERIOD_ROLLING_12) {
    return "mira.baseline.twelveMonthsBefore";
  }
  return periodKey === MIRA_PERIOD_YEAR_TO_DATE ? "mira.baseline.sameMonthsLastYear" : "mira.baseline.previousYear";
}

/** The whole months from `from` up to the exclusive `toExclusive`, both `YYYY-MM-DD` on a month's first. */
export function monthsOfRange(from: string, toExclusive: string): number {
  const [fromYear, fromMonth] = from.split("-").map(Number);
  const [toYear, toMonth] = toExclusive.split("-").map(Number);
  return Math.max(0, toYear * 12 + toMonth - (fromYear * 12 + fromMonth));
}

/**
 * The factor a total of the baseline is multiplied by to compare with the chosen period: their lengths in
 * months (a month against twelve: a twelfth, Taimoor 2026-11-02). 1 where both are as long, and 1 where a
 * length is unknown, so nothing is ever scaled by accident.
 */
export function baselineScale(
  chosen: Pick<MiraPublishedPeriod, "from" | "toExclusive">,
  baseline: Pick<MiraPublishedPeriod, "from" | "toExclusive">
): number {
  const chosenMonths = monthsOfRange(chosen.from, chosen.toExclusive);
  const baselineMonths = monthsOfRange(baseline.from, baseline.toExclusive);
  return chosenMonths > 0 && baselineMonths > 0 ? chosenMonths / baselineMonths : 1;
}

/**
 * The baseline period with every total scaled to the chosen period's length: its value and the numbers of its
 * breakdown, never its case count. Averages and shares compare as they are. The same object where the scale
 * is 1.
 */
export function scaledBaselinePeriod(baseline: MiraPublishedPeriod, scale: number, countIds: readonly string[]): MiraPublishedPeriod {
  if (scale === 1) {
    return baseline;
  }
  const metrics: MiraPublishedPeriod["metrics"] = {};
  Object.entries(baseline.metrics).forEach(([metricId, metric]) => {
    if (!countIds.includes(metricId)) {
      metrics[metricId] = metric;
      return;
    }
    const breakdown = metric.breakdown
      ? Object.fromEntries(
          Object.entries(metric.breakdown).map(([key, value]) => [key, typeof value === "number" ? value * scale : value])
        )
      : metric.breakdown;
    metrics[metricId] = {
      ...metric,
      ...(typeof metric.value === "number" ? { value: metric.value * scale } : {}),
      ...(breakdown ? { breakdown } : {}),
    };
  });
  return { ...baseline, metrics };
}
